<template>
    <div>
        <w-app
            :title="$t('module.validation_work_flow_action')"
            show-total
            :total="total"
        >
            <template v-slot:rightToolbar>
                <w-btn-reload :loading="loading" @press="findAll"/>
                <w-btn-add
                    :show="securityStore.authorities.UPDATE_WORKER"
                    @press="$router.push({ name: 'validation_work_flow_actions.set_validation_work_flow' })"
                />
            </template>

            <template v-slot:content>
                <v-container fluid>
                    <ValidationWorkFlowActionFilter :filter="filter"/>
                </v-container>
                <v-skeleton-loader
                    v-show="loading"
                    ref="skeleton"
                    :type="'table'"
                    :class="`mx-auto elevation-${tableElevation}`"
                ></v-skeleton-loader>
                <v-data-table
                    v-show="!loading"
                    :headers="headers"
                    :items="actionStore.actions"
                    :sort-by.sync="sortBy"
                    :sort-desc.sync="descending"
                    :server-items-length="total"
                    :loading="loading"
                    must-sort
                    :multi-sort="false"
                    hide-default-footer
                    :class="`elevation-${tableElevation}`"
                    dense
                    @dblclick:row="view"
                    :item-class="rowColor"
                    @click:row="itemClick"
                >
                    <template v-slot:[`item.action`]="{ item }">
                        <v-menu bottom right>
                            <template v-slot:activator="{ on }">
                                <v-btn
                                    depressed
                                    dark
                                    icon
                                    color="primary"
                                    v-on="on"
                                    @click="itemClick(item)"
                                >
                                    <v-icon>mdi-dots-horizontal</v-icon>
                                </v-btn>
                            </template>

                            <v-list nav dense>
                                <v-list-item
                                    @click="edit(item)"
                                >
                                    <v-list-item-icon>
                                        <v-icon
                                            v-text="'mdi-pencil-box-multiple'"
                                            color="orange"
                                        ></v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-title>{{ $t("common.edit") }}</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </template>
                    <template v-slot:[`item.postDateTime`]="{ item }">
                        <w-chip color="teal" outlined :text="formatedDateTime(item.postDateTime)"/>
                    </template>
                    <template v-slot:[`item.validationWorkFlowName`]="{ item }">
                        <w-chip color="blue darken-2" outlined :text="item.validationWorkFlowName"/>
                    </template>
                    <template v-slot:[`item.modules`]="{ item }">
                        <w-chip
                            v-for="(module, index) in item.modules"
                            :key="`mdl-${index}`"
                            outlined
                            :text="$t(`module.${module.toLowerCase()}`)"
                        />
                    </template>
                    <template v-slot:[`item.steps`]="{ item }">
                        <w-chip
                            v-for="(step, index) in item.steps"
                            :key="`stp-${index}`"
                            outlined
                            :text="step.validatorFullName"
                        />
                    </template>
                    <template v-slot:footer="{}">
                        <br/>
                        <div class="text-center">
                            <v-row justify="center">
                                <v-col md="6">
                                    <v-pagination
                                        v-model="page"
                                        :length="Math.ceil(total / itemsPerPage)"
                                        total-visible="15"
                                        @input="findAll"
                                    />
                                </v-col>
                                <v-col md="2">
                                    <w-enum-select
                                        v-model="itemsPerPage"
                                        :items="paginationListLength"
                                        :label="$t('common.items_per_page')"
                                        @change="findAll"
                                    />
                                </v-col>
                            </v-row>
                        </div>
                    </template>
                </v-data-table>
            </template>
        </w-app>

        <router-view/>
    </div>
</template>

<script>
import {onMounted, ref, watch} from "vue";
import useSharedData from "@/use/SharedData.js";
import useSharedMethod from "@/use/SharedMethod.js";
import useTools from "@/use/Tools.js";
import {useSecurityStore} from "@/stores/Security.js";
import {useValidationWorkFlowActionStore} from "@/stores/Request/ValidationWorkFlowAction.js";
import ValidationWorkFlowActionFilter from "@/views/Request/ValidationWorkFlowAction/Components/ValidationWorkFlowActionFilter.vue";
import axios from "axios";


export default {
    components: {ValidationWorkFlowActionFilter},
    setup() {
        const {rowColor, itemClick, transformFilterValues, formatedDateTime} = useSharedMethod();
        const {$router, $i18n} = useTools();
        const {
            filter,
            loading,
            total,
            itemsPerPage,
            page,
            sortBy,
            descending,
            tableElevation,
            paginationListLength,
        } = useSharedData();
        sortBy.value = "postDateTime";
        descending.value = true;
        const rightDrawer = ref(false);
        const headers = [
            {
                text: "...",
                sortable: false,
                align: "left",
                value: "action",
                width: "0%",
            },
            {
                text: $i18n.t("common.date"),
                align: "left",
                value: "postDateTime",
                sortable: true,
            },
            {
                text: $i18n.t("module.validation_work_flow"),
                align: "left",
                value: "validationWorkFlowName",
                sortable: true,
            },
            {
                text: $i18n.t("common.modules"),
                align: "left",
                value: "modules",
                sortable: true,
            },
            {
                text: $i18n.t("common.validators"),
                align: "left",
                value: "steps",
                sortable: true,
            },
        ];

        const actionStore = useValidationWorkFlowActionStore();
        const securityStore = useSecurityStore();

        function findAll() {
            loading.value = true;
            filter.value.page = page.value - 1;
            filter.value.rowsPerPage = itemsPerPage.value;
            filter.value.sortBy = sortBy.value;
            filter.value.descending = descending.value;
            axios.get('/validationWorkFlowActions', {params: transformFilterValues(filter.value)}).then((response) => {
                actionStore.setActions(response.data.elements);
                total.value = response.data.total;
            }).finally(() => {
                loading.value = false;
            });
        }

        function view(event, {item}) {
            $router.push({name: "validation_work_flow_actions.view", params: {id: item.id}});
        }

        onMounted(() => {
            findAll();
        });

        watch([sortBy, descending], () => {
            findAll();
        });

        watch(
            filter.value,
            () => {
                page.value = 1;
                findAll();
            },
            {deep: true}
        );

        function edit(action) {
            $router.push({
                name: 'validation_work_flow_actions.set_validation_work_flow.action',
                params: {actionId: action.id}
            });
        }

        return {
            securityStore,
            actionStore,
            headers,
            filter,
            loading,
            total,
            rightDrawer,
            itemsPerPage,
            page,
            sortBy,
            descending,
            tableElevation,
            paginationListLength,
            formatedDateTime,
            edit,
            findAll,
            view,
            rowColor,
            itemClick,
        };
    },
};
</script>